import { useId, type ReactNode } from 'react';
import { IMAGE_WIDTHS, type ImageWidth } from '@oscom/shared';
import { moveBlock, newBlock, type EditorBlock } from '../documents/editorBlocks';
import { RichTextEditor } from './RichTextEditor';

const TITLES: Record<EditorBlock['type'], string> = {
  richText: 'Text',
  image: 'Bild',
  note: 'Hinweis',
  warning: 'Warnung',
};
const WIDTH_LABELS: Record<ImageWidth, string> = {
  100: 'Volle Breite',
  50: 'Halbe Breite (½)',
  33: 'Ein Drittel (⅓)',
};

/** Hand blocks of one chapter, one below the other; "+" adds text, image, note or warning. */
export function BlockListEditor({
  blocks,
  onChange,
  onUpload,
  onRetryUpload,
  imageUrl,
  revision,
}: {
  blocks: readonly EditorBlock[];
  onChange: (blocks: EditorBlock[]) => void;
  onUpload: (blockId: string, file: File) => void;
  onRetryUpload: (blockId: string) => void;
  imageUrl: (assetId: string) => string;
  /** Changes when the chapter was reloaded: text editors mount anew. */
  revision: number;
}): ReactNode {
  const replace = (index: number, block: EditorBlock): void =>
    onChange(blocks.map((entry, position) => (position === index ? block : entry)));

  return (
    <div className="block-list">
      {blocks.map((block, index) => {
        const name = `${TITLES[block.type]} ${index + 1}`;
        return (
          <section
            key={block.id}
            className={`block-editor block-editor--${block.type}`}
            aria-label={name}
          >
            <div className="block-editor__bar">
              <span className="block-editor__type">{TITLES[block.type]}</span>
              <button
                type="button"
                className="button button--ghost"
                aria-label={`${name} nach oben`}
                disabled={index === 0}
                onClick={() => onChange(moveBlock(blocks, index, -1))}
              >
                Hoch
              </button>
              <button
                type="button"
                className="button button--ghost"
                aria-label={`${name} nach unten`}
                disabled={index === blocks.length - 1}
                onClick={() => onChange(moveBlock(blocks, index, 1))}
              >
                Runter
              </button>
              <button
                type="button"
                className="button button--ghost"
                aria-label={`${name} löschen`}
                onClick={() => onChange(blocks.filter((_, position) => position !== index))}
              >
                Löschen
              </button>
            </div>
            {block.type === 'image' ? (
              <ImageBlockFields
                block={block}
                imageUrl={imageUrl}
                onChange={(next) => replace(index, next)}
                onUpload={(file) => onUpload(block.id, file)}
                onRetry={() => onRetryUpload(block.id)}
              />
            ) : (
              <RichTextEditor
                key={`${block.id}-${revision}`}
                value={block.content}
                label={name}
                onChange={(content) => replace(index, { ...block, content })}
              />
            )}
          </section>
        );
      })}
      <div className="block-list__add" role="group" aria-label="Block hinzufügen">
        {(['richText', 'image', 'note', 'warning'] as const).map((type) => (
          <button
            key={type}
            type="button"
            className="button"
            onClick={() => onChange([...blocks, newBlock(type)])}
          >
            + {TITLES[type]}
          </button>
        ))}
      </div>
    </div>
  );
}

function ImageBlockFields({
  block,
  imageUrl,
  onChange,
  onUpload,
  onRetry,
}: {
  block: Extract<EditorBlock, { type: 'image' }>;
  imageUrl: (assetId: string) => string;
  onChange: (block: Extract<EditorBlock, { type: 'image' }>) => void;
  onUpload: (file: File) => void;
  onRetry: () => void;
}): ReactNode {
  const ids = { file: useId(), alt: useId(), caption: useId(), width: useId() };
  return (
    <div className="stack">
      {block.assetId ? (
        <img
          className="doc-image__preview"
          src={imageUrl(block.assetId)}
          alt={block.alternativeText || 'Bild ohne Alternativtext'}
        />
      ) : null}
      {block.uploading ? (
        <p className="muted" role="status">
          Wird hochgeladen …
        </p>
      ) : null}
      {block.uploadError ? (
        <div className="notice notice--warning" role="alert">
          <p>{block.uploadError}</p>
          <button type="button" className="button" onClick={onRetry}>
            Hochladen fehlgeschlagen – erneut versuchen
          </button>
        </div>
      ) : null}
      <div className="form-field">
        <label htmlFor={ids.file}>
          {block.assetId ? 'Anderes Bild wählen (PNG oder JPEG)' : 'Bild wählen (PNG oder JPEG)'}
        </label>
        <input
          id={ids.file}
          type="file"
          accept="image/png,image/jpeg"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
          }}
        />
      </div>
      <div className="form-field">
        <label htmlFor={ids.alt}>Alternativtext (Pflicht)</label>
        <input
          id={ids.alt}
          type="text"
          maxLength={300}
          aria-required="true"
          value={block.alternativeText}
          onChange={(event) => onChange({ ...block, alternativeText: event.target.value })}
        />
      </div>
      <div className="form-field">
        <label htmlFor={ids.caption}>Bildunterschrift</label>
        <input
          id={ids.caption}
          type="text"
          maxLength={300}
          value={block.caption}
          onChange={(event) => onChange({ ...block, caption: event.target.value })}
        />
      </div>
      <div className="form-field">
        <label htmlFor={ids.width}>Breite</label>
        <select
          id={ids.width}
          value={block.widthPercent}
          onChange={(event) =>
            onChange({ ...block, widthPercent: Number(event.target.value) as ImageWidth })
          }
        >
          {IMAGE_WIDTHS.map((width) => (
            <option key={width} value={width}>
              {WIDTH_LABELS[width]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
